import dayjs from "dayjs";
import { AxiosError } from "axios";

export type ClientExportPlatform = "ios" | "android";

export type ClientExportFilters = {
  platform?: ClientExportPlatform | "";
  search?: string;
  country?: string;
  createdFrom?: string;
  createdTo?: string;
};

const MAX_FIELD_LENGTH = 100;

const isValidDateInput = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime());
};

export const validateClientExportFilters = (
  filters: ClientExportFilters
): string | null => {
  const search = filters.search?.trim() ?? "";
  const country = filters.country?.trim() ?? "";

  if (search.length > MAX_FIELD_LENGTH) {
    return "Search must be 100 characters or less.";
  }
  if (country.length > MAX_FIELD_LENGTH) {
    return "Country must be 100 characters or less.";
  }
  if (
    filters.platform &&
    filters.platform !== "ios" &&
    filters.platform !== "android"
  ) {
    return "Platform must be iOS or Android.";
  }
  if (filters.createdFrom && !isValidDateInput(filters.createdFrom)) {
    return "Invalid start date.";
  }
  if (filters.createdTo && !isValidDateInput(filters.createdTo)) {
    return "Invalid end date.";
  }
  if (filters.createdFrom && filters.createdTo) {
    const from = dayjs(filters.createdFrom);
    const to = dayjs(filters.createdTo);
    if (from.isAfter(to, "day")) {
      return "Start date must be on or before end date.";
    }
  }
  return null;
};

export const buildClientExportQueryParams = (
  filters: ClientExportFilters
): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.platform === "ios" || filters.platform === "android") {
    params.set("platform", filters.platform);
  }

  const search = filters.search?.trim();
  if (search) params.set("search", search);

  const country = filters.country?.trim();
  if (country) params.set("country", country);

  if (filters.createdFrom) {
    params.set("createdFrom", `${filters.createdFrom}T00:00:00.000Z`);
  }
  if (filters.createdTo) {
    params.set("createdTo", `${filters.createdTo}T23:59:59.999Z`);
  }

  return params;
};

const parseErrorPayload = (text: string): string | null => {
  try {
    const json = JSON.parse(text) as {
      error?: { message?: string };
      message?: string;
    };
    return json.error?.message ?? json.message ?? null;
  } catch {
    return text.trim() || null;
  }
};

export const getClientsExportErrorMessage = async (
  error: AxiosError<Blob>
): Promise<string> => {
  const status = error.response?.status;
  const data = error.response?.data;

  if (data instanceof Blob) {
    const text = await data.text();
    const parsed = parseErrorPayload(text);
    if (parsed) return parsed;
  }

  if (status === 403) return "Only admins can export clients.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 400)
    return "Invalid export filters. Please check your inputs.";
  if (status === 500) return "Export failed. Please try again.";

  return "Failed to generate export.";
};
